import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, map, of } from 'rxjs';

import { environment } from '../../environments/environment';
import { Envelope, GalleryItem } from './models';

/**
 * Published before and after photos, fetched once per visit and shared by the
 * header (which only links to the gallery when there is something in it), the
 * home page preview and the gallery page.
 */
@Injectable({ providedIn: 'root' })
export class GalleryService {
  private readonly http = inject(HttpClient);

  private readonly state = signal<GalleryItem[] | null>(null);
  private requested = false;

  /** null until the first response arrives. */
  readonly items = this.state.asReadonly();

  readonly failed = signal(false);

  readonly hasItems = computed(() => (this.state()?.length ?? 0) > 0);

  load(): void {
    if (this.requested) {
      return;
    }

    this.requested = true;

    this.http
      .get<Envelope<GalleryItem[]>>(`${environment.apiUrl}/public/gallery`)
      .pipe(
        map((response) => response.data),
        // An unreachable API just means no gallery link, not a broken page.
        catchError(() => {
          this.failed.set(true);

          return of([]);
        }),
      )
      .subscribe((items) => this.state.set(items));
  }
}
